import React, {useCallback, useContext, useEffect, useState} from "react";
import {toast} from "react-toastify";
import moment from "moment";
import {
    Autocomplete, Box, Button, ButtonBase, Collapse, Fade, IconButton, InputBase, Modal,
    Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
    Add01Icon, ArrowDown01Icon, Cancel01Icon, Delete02Icon, Key01Icon, PencilEdit01Icon, Search01Icon
} from "@hugeicons/core-free-icons";
import {OrganizationRepository} from "../../repositories/OrganizationRepository";
import {SignInContext} from "../../context/SignInContext";
import useConfirm from "../../hooks/useConfirm";
import axios from "../../config/axios";
import {OrganizationApiKeys} from "../api-keys/components/organization-api-keys";
import {useCollectionView} from "../../hooks/useCollectionView";
import {CollectionViewToggle} from "../../components/CollectionViewToggle";
import {controlSx, Panel} from "../registry-project/dashboard/components/Panel";
import {
    FieldLabel, FlatAutocompleteInput, modalCancelSx, ModalButton, modalPaperSx, modalSaveSx,
    controlSx as formControlSx,
} from "../../components/modals/formFieldShared";

const headCellSx = {
    height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
    bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1.25, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7"};
const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};
const deleteIconButtonSx = {...iconButtonSx, "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)", color: "#DC2626"}};
const headerButtonSx = {
    height: 38, px: 1.75, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 700,
    color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};
const smallActionSx = {
    height: 30, px: 1, gap: 0.5, borderRadius: "8px", fontSize: 12, fontWeight: 600, color: "#6B6B80",
    "&:hover": {bgcolor: "#F4F4FA"},
};

// Clickable status pill, replaces the plain on/off switch for activating/deactivating an organization
const StatusPill = ({active, onClick}) => (
    <Tooltip title={active ? "Click to deactivate" : "Click to activate"}>
        <ButtonBase
            onClick={onClick}
            sx={{
                height: 26, px: 1.1, gap: 0.65, borderRadius: "999px", fontSize: 12, fontWeight: 600,
                color: active ? "#2F9A6A" : "#8A8AA0",
                bgcolor: active ? "rgba(47, 154, 106, 0.12)" : "rgba(138, 138, 160, 0.12)",
                "&:hover": {bgcolor: active ? "rgba(47, 154, 106, 0.2)" : "rgba(138, 138, 160, 0.2)"},
            }}
        >
            <Box sx={{width: 6, height: 6, borderRadius: "50%", bgcolor: active ? "#2F9A6A" : "#8A8AA0", flexShrink: 0}}/>
            {active ? "Active" : "Inactive"}
        </ButtonBase>
    </Tooltip>
);

// Small circular chevron that springs 180° open - used to expand a row/card's API keys section
const ExpandToggle = ({expanded, onClick, title}) => (
    <Tooltip title={title}>
        <IconButton
            size={"small"} onClick={onClick}
            sx={{
                width: 26, height: 26, borderRadius: "50% !important",
                color: expanded ? "#4B55C9" : "#8A8AA0",
                bgcolor: expanded ? "rgba(132, 145, 247, 0.12)" : "#FAFAFD",
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease",
                "&:hover": {bgcolor: "rgba(132, 145, 247, 0.18)"},
            }}
        >
            <HugeiconsIcon icon={ArrowDown01Icon} size={16}/>
        </IconButton>
    </Tooltip>
);

const MAX_AVATARS = 3;

const memberBadgeSx = {
    width: 34, height: 34, flexShrink: 0, borderRadius: "50%", bgcolor: "#F2F2F7", color: "#4A4A63",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700,
};

const initialsOf = (username) => (username || "").trim().slice(0, 2).toUpperCase() || "?";

const MemberAvatars = ({members}) => {
    if (!members?.length) {
        return <Typography sx={{fontSize: 13, color: "#8A8AA0"}}>-</Typography>;
    }
    const visible = members.slice(0, MAX_AVATARS);
    const surplus = members.length - visible.length;
    return (
        <Tooltip title={members.join(", ")}>
            <Stack direction={"row"} gap={0.75} flexWrap={"wrap"} sx={{width: "fit-content"}}>
                {visible.map(member => (
                    <Box key={member} sx={memberBadgeSx}>{initialsOf(member)}</Box>
                ))}
                {surplus > 0 && (
                    <Box sx={{...memberBadgeSx, color: "#8A8AA0", fontSize: 11}}>+{surplus}</Box>
                )}
            </Stack>
        </Tooltip>
    );
};

const SkeletonCard = () => (
    <Box sx={{bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "16px", p: 2.5, display: "flex", flexDirection: "column", gap: 1.5}}>
        <Skeleton height={20} width={"60%"} sx={{borderRadius: "6px"}}/>
        <Skeleton height={14} width={"40%"} sx={{borderRadius: "6px"}}/>
        <Skeleton height={32} sx={{borderRadius: "10px", mt: 0.5}}/>
    </Box>
);

const OrganizationCard = ({organization, onToggleActive, onEdit, onDelete, expanded, onToggleExpanded}) => (
    <Box sx={{
        bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "16px", display: "flex", flexDirection: "column",
        gap: 1.5, p: 2.5, minWidth: 0, transition: "box-shadow 0.15s ease, border-color 0.15s ease",
        "&:hover": {boxShadow: "0 2px 8px rgba(35, 38, 77, 0.06)", borderColor: "#D7DBFB"},
    }}>
        <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} gap={1}>
            <Box sx={{minWidth: 0}}>
                <Typography noWrap sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>{organization?.name}</Typography>
                {organization?.type && (
                    <Box component={"span"} sx={{
                        display: "inline-block", mt: 0.5, px: 0.9, py: 0.25, borderRadius: "999px", fontSize: 11,
                        fontWeight: 700, color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)",
                    }}>
                        {organization.type}
                    </Box>
                )}
            </Box>
            <StatusPill active={!!organization?.active} onClick={onToggleActive}/>
        </Stack>

        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <MemberAvatars members={organization?.members?.map(i => i?.member) || []}/>
            <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                {organization?.dateCreated ? moment(organization.dateCreated).format("DD MMM YYYY") : "—"}
            </Typography>
        </Stack>

        <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{pt: 1, borderTop: "1px solid #F0F0F6"}}>
            <ButtonBase onClick={onToggleExpanded} sx={{...smallActionSx, mr: "auto"}}>
                <HugeiconsIcon icon={Key01Icon} size={14}/>
                API Keys
            </ButtonBase>
            <IconButton size={"small"} sx={iconButtonSx} onClick={onEdit}>
                <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
            </IconButton>
            <IconButton size={"small"} sx={deleteIconButtonSx} onClick={onDelete}>
                <HugeiconsIcon icon={Delete02Icon} size={16}/>
            </IconButton>
        </Stack>

        <Collapse in={expanded} timeout={"auto"} unmountOnExit>
            <Box sx={{pt: 1.5}}>
                <OrganizationApiKeys organization={organization}/>
            </Box>
        </Collapse>
    </Box>
);

// Organizations: who they are, who's in them, and their API keys - as a table or as cards.
export const RegistryOrganizationsPage = () => {
    const {currentUser, updateOrganizations: refreshGlobalOrganizations} = useContext(SignInContext);
    const isAdmin = currentUser?.roles?.includes("admin");

    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [viewMode, setViewMode] = useCollectionView("organizations-view-mode", "list");
    const [openModal, setOpenModal] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const confirmDelete = useConfirm("Are you sure you want to delete this organization?");

    const fetchOrganizations = useCallback(() => {
        setLoading(true);
        const request = isAdmin ? OrganizationRepository.getAll() : OrganizationRepository.getMyOrganizations();
        request
            .then(response => setOrganizations(response?.data || []))
            .catch(() => toast.error("Failed to load organizations"))
            .finally(() => setLoading(false));
    }, [isAdmin]);

    useEffect(() => {
        fetchOrganizations();
    }, [fetchOrganizations]);

    const onFetchSuccess = () => {
        fetchOrganizations();
        refreshGlobalOrganizations();
    };

    const search = query.trim().toLowerCase();
    const filteredOrganizations = organizations.filter(organization => !search
        || organization?.name?.toLowerCase()?.includes(search)
        || organization?.type?.toLowerCase()?.includes(search)
        || organization?.members?.some(m => m?.member?.toLowerCase()?.includes(search)));

    const onToggleActive = (organization) => {
        OrganizationRepository.edit(organization?.id, {
            name: organization?.name, type: organization?.type, active: !organization?.active,
        })
            .then(onFetchSuccess)
            .catch(() => toast.error("Failed to update organization status"));
    };

    const onEdit = (organization) => {
        setSelectedOrganization(organization);
        setOpenModal(true);
    };

    const onToggleExpanded = (id) => setExpandedId(prev => prev === id ? null : id);

    const handleDelete = async (organization) => {
        const confirmed = await confirmDelete({title: `Are you sure you want to delete "${organization?.name}"?`});
        if (!confirmed) return;
        OrganizationRepository.delete(organization?.id)
            .then(() => {
                toast.success("Successfully deleted organization");
                onFetchSuccess();
            })
            .catch(() => toast.error("Failed to delete organization"));
    };

    const emptyMessage = search ? `No organizations match "${query}"` : "No organizations yet";

    return (
        <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: 1200, width: "100%", mx: "auto", boxSizing: "border-box"}}>
            <CreateOrganizationModal
                open={openModal}
                organizations={organizations}
                selectedOrganization={selectedOrganization}
                handleClose={() => {
                    setOpenModal(false);
                    setSelectedOrganization(null);
                }}
                onSuccess={onFetchSuccess}
            />

            <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
                <Box>
                    <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>ADMINISTRATION</Typography>
                    <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
                        Organizations
                    </Typography>
                    <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
                        Manage organizations, their members and API access
                    </Typography>
                </Box>
                <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
                    <Box component={"label"} sx={{...controlSx, width: 220}}>
                        <HugeiconsIcon icon={Search01Icon} size={16}/>
                        <InputBase
                            placeholder={"Search organizations..."}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            inputProps={{"aria-label": "Search organizations"}}
                            sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                        />
                    </Box>
                    <CollectionViewToggle value={viewMode} onChange={setViewMode}/>
                    <Button
                        startIcon={<HugeiconsIcon icon={Add01Icon} size={17}/>}
                        onClick={() => {
                            setSelectedOrganization(null);
                            setOpenModal(true);
                        }}
                        sx={headerButtonSx}
                    >
                        Create Organization
                    </Button>
                </Stack>
            </Stack>

            {viewMode === "cards" ? (
                <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)"}, gap: 2}}>
                    {loading && organizations.length === 0 && Array.from({length: 6}).map((_, index) => <SkeletonCard key={index}/>)}
                    {!loading && filteredOrganizations.map(organization => (
                        <OrganizationCard
                            key={organization.id}
                            organization={organization}
                            onToggleActive={() => onToggleActive(organization)}
                            onEdit={() => onEdit(organization)}
                            onDelete={() => handleDelete(organization)}
                            expanded={expandedId === organization.id}
                            onToggleExpanded={() => onToggleExpanded(organization.id)}
                        />
                    ))}
                    {!loading && filteredOrganizations.length === 0 && (
                        <Box sx={{gridColumn: "1 / -1", py: 6, textAlign: "center", color: "#8A8AA0", fontSize: 13}}>
                            {emptyMessage}
                        </Box>
                    )}
                </Box>
            ) : (
                <Panel sx={{overflow: "hidden"}}>
                    <TableContainer sx={{overflowX: "auto"}}>
                        <Table size={"small"} sx={{minWidth: 820}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding={"checkbox"} sx={headCellSx}/>
                                    <TableCell sx={{...headCellSx, pl: 2.5}}>NAME</TableCell>
                                    <TableCell sx={headCellSx}>TYPE</TableCell>
                                    <TableCell sx={headCellSx}>MEMBERS</TableCell>
                                    <TableCell sx={headCellSx}>CREATED</TableCell>
                                    <TableCell sx={headCellSx}>ACTIVE</TableCell>
                                    <TableCell sx={{...headCellSx, pr: 2.5}}/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && organizations.length === 0 && Array.from({length: 5}).map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell colSpan={7} sx={{...cellSx, px: 2.5}}><Skeleton height={32}/></TableCell>
                                    </TableRow>
                                ))}
                                {!loading && filteredOrganizations.map(organization => {
                                    const expanded = expandedId === organization.id;
                                    return (
                                        <React.Fragment key={organization.id}>
                                            <TableRow hover sx={{
                                                "&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"},
                                                ...(expanded && {bgcolor: "rgba(132, 145, 247, 0.05)"}),
                                            }}>
                                                <TableCell padding={"checkbox"} sx={cellSx}>
                                                    <ExpandToggle
                                                        expanded={expanded}
                                                        title={expanded ? "Hide API keys" : "Show API keys"}
                                                        onClick={() => onToggleExpanded(organization.id)}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{...cellSx, pl: 2.5}}>
                                                    <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{organization.name}</Typography>
                                                    {organization.description && (
                                                        <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>{organization.description}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    {organization.type && (
                                                        <Box component={"span"} sx={{
                                                            px: 1, py: 0.25, borderRadius: "999px", fontSize: 11, fontWeight: 600,
                                                            color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)",
                                                        }}>
                                                            {organization.type}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    <MemberAvatars members={organization?.members?.map(i => i?.member) || []}/>
                                                </TableCell>
                                                <TableCell sx={{...cellSx, color: "#8A8AA0", whiteSpace: "nowrap"}}>
                                                    {organization.dateCreated ? moment(organization.dateCreated).format("DD MMM YYYY") : "—"}
                                                </TableCell>
                                                <TableCell sx={cellSx}>
                                                    <StatusPill active={!!organization.active} onClick={() => onToggleActive(organization)}/>
                                                </TableCell>
                                                <TableCell padding={"checkbox"} sx={{...cellSx, pr: 2.5, whiteSpace: "nowrap"}}>
                                                    <Stack direction={"row"} gap={0.25} justifyContent={"flex-end"}>
                                                        <Tooltip title={"Edit"}>
                                                            <IconButton size={"small"} sx={iconButtonSx} onClick={() => onEdit(organization)}>
                                                                <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={"Delete"}>
                                                            <IconButton size={"small"} sx={deleteIconButtonSx} onClick={() => handleDelete(organization)}>
                                                                <HugeiconsIcon icon={Delete02Icon} size={16}/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{py: 0, border: 0}}>
                                                    <Collapse in={expanded} timeout={"auto"} unmountOnExit>
                                                        <Box sx={{p: 2.5, bgcolor: "#FAFAFD"}}>
                                                            <OrganizationApiKeys organization={organization}/>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                                {!loading && filteredOrganizations.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Panel>
            )}
        </Stack>
    );
};

export const CreateOrganizationModal = ({open, handleClose, selectedOrganization, onSuccess, organizations}) => {
    const [organization, setOrganization] = useState({name: "", type: "", members: [], active: true});
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedImportOrganization, setSelectedImportOrganization] = useState(null);
    const [loading, setLoading] = useState(false);

    const resetModalState = () => {
        setOrganization({name: "", type: "", members: [], active: true});
        setSelectedUser(null);
        setSelectedImportOrganization(null);
    };

    const closeModal = () => {
        resetModalState();
        handleClose();
    };

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await axios.get("/api/user/all");
                const activeUsers = response.data?.filter(u => u?.enabled);
                setUsers([...(new Set(activeUsers?.map(u => u.username)) || [])]);
            } catch (err) {
                toast.error("Failed to get users");
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        setOrganization({
            name: selectedOrganization?.name || "",
            type: selectedOrganization?.type || "",
            active: selectedOrganization?.active ?? true,
            members: selectedOrganization?.members?.map(item => item?.member) || [],
        });
    }, [selectedOrganization]);

    const onChange = (e) => {
        const key = e.target.name;
        const value = e.target.value;
        setOrganization(prev => ({...prev, [key]: value}));
    };

    const updateMembers = async (id) => {
        const currentMembers = selectedOrganization?.members || [];
        const added = organization.members.filter(username => !currentMembers.some(m => m?.member === username));
        const removed = currentMembers.filter(m => !organization.members.includes(m?.member));

        await Promise.all([
            ...added.map(username => OrganizationRepository.addMember(id, username)),
            ...removed.map(m => OrganizationRepository.removeMember(id, m?.id)),
        ]);
    };

    const handleAddMember = (newUser) => {
        if (newUser && !organization?.members?.includes(newUser)) {
            setOrganization(prev => ({...prev, members: [...(prev.members || []), newUser]}));
            setSelectedUser(null);
        }
    };

    const handleImportMembers = (sourceOrganization) => {
        setSelectedImportOrganization(sourceOrganization);
        const importedMembers = sourceOrganization?.members
            ?.map(member => typeof member === "string" ? member : member?.member)
            ?.filter(Boolean) || [];

        setOrganization(prev => ({
            ...prev,
            members: [...new Set([...(prev.members || []), ...importedMembers])],
        }));
    };

    const handleRemoveAllMembers = () => {
        setOrganization(prev => ({...prev, members: []}));
    };

    const handleRemoveMember = (memberToRemove) => {
        setOrganization(prev => ({...prev, members: prev.members.filter(member => member !== memberToRemove)}));
    };

    const availableUsers = users.filter(user => !organization?.members?.includes(user));
    const isEdit = Boolean(selectedOrganization?.id);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!organization?.name) {
            toast.error("Organization name is required!");
            return;
        }
        setLoading(true);
        try {
            if (isEdit) {
                const {members, ...details} = organization;
                await OrganizationRepository.edit(selectedOrganization?.id, details);
                await updateMembers(selectedOrganization?.id);
                onSuccess();
                toast.success("Successfully updated organization");
                closeModal();
            } else {
                await OrganizationRepository.create(organization);
                onSuccess();
                toast.success("Successfully created organization");
                closeModal();
            }
        } catch (error) {
            toast.error(error?.message || `Failed to ${isEdit ? "update" : "create"} organization`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={loading ? undefined : closeModal}>
            <Fade in={open}>
                <Box component={"form"} onSubmit={handleSubmit} sx={modalPaperSx(480)}>
                    <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} sx={{p: 3, pb: 2}}>
                        <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
                            {isEdit ? "Update" : "Create"} Organization
                        </Typography>
                        <IconButton size={"small"} onClick={loading ? undefined : closeModal} sx={{color: "#8A8AA0"}}>
                            <HugeiconsIcon icon={Cancel01Icon} size={18}/>
                        </IconButton>
                    </Stack>

                    <Stack gap={2} sx={{px: 3, pb: 1, overflow: "auto"}}>
                        <Box>
                            <FieldLabel required>Name</FieldLabel>
                            <Box sx={formControlSx}>
                                <InputBase
                                    name={"name"} autoFocus value={organization?.name} onChange={onChange}
                                    inputProps={{"aria-label": "Name"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                                />
                            </Box>
                        </Box>
                        <Box>
                            <FieldLabel required>Type</FieldLabel>
                            <Box sx={formControlSx}>
                                <InputBase
                                    name={"type"} value={organization?.type} onChange={onChange}
                                    inputProps={{"aria-label": "Type"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <FieldLabel>Import members from organization</FieldLabel>
                            <Autocomplete
                                options={organizations?.filter(item => item?.id !== selectedOrganization?.id) || []}
                                getOptionLabel={item => item?.name || ""}
                                value={selectedImportOrganization}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                onChange={(event, newValue) => handleImportMembers(newValue)}
                                renderInput={(props) => <FlatAutocompleteInput {...props} placeholder={"Select an organization..."}/>}
                            />
                            <Typography sx={{fontSize: 12, color: "#8A8AA0", mt: 0.5}}>Adds members to the current selection</Typography>
                        </Box>

                        <Box>
                            <FieldLabel>Add Member</FieldLabel>
                            <Autocomplete
                                renderInput={(props) => <FlatAutocompleteInput {...props} placeholder={"Search users..."}/>}
                                options={availableUsers}
                                getOptionLabel={option => option}
                                value={selectedUser}
                                clearOnBlur
                                blurOnSelect
                                onChange={(e, newValue) => handleAddMember(newValue)}
                                onBlur={() => setSelectedUser(null)}
                            />
                        </Box>

                        {organization?.members?.length > 0 && (
                            <Table size={"small"} sx={{borderRadius: "12px !important", overflow: "hidden"}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headCellSx}>Username</TableCell>
                                        <TableCell sx={headCellSx} align={"right"}>
                                            <ButtonBase
                                                type={"button"} onClick={handleRemoveAllMembers}
                                                sx={{
                                                    height: 26, px: 1, borderRadius: "8px", fontSize: 12, fontWeight: 600, color: "#DC2626",
                                                    "&:hover": {bgcolor: "rgba(220, 38, 38, 0.08)"},
                                                }}
                                            >
                                                Remove all
                                            </ButtonBase>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {organization?.members?.map((member, index) => (
                                        <TableRow key={index}>
                                            <TableCell sx={cellSx}>{member}</TableCell>
                                            <TableCell align={"right"} sx={cellSx}>
                                                <IconButton size={"small"} sx={deleteIconButtonSx} onClick={() => handleRemoveMember(member)}>
                                                    <HugeiconsIcon icon={Delete02Icon} size={16}/>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Stack>

                    <Stack direction={"row"} gap={1.5} justifyContent={"flex-end"} sx={{p: 3, pt: 2}}>
                        <ModalButton onClick={loading ? undefined : closeModal} sx={modalCancelSx}>Cancel</ModalButton>
                        <ModalButton type={"submit"} disabled={loading} sx={modalSaveSx}>
                            {isEdit ? "Update" : "Create"}
                        </ModalButton>
                    </Stack>
                </Box>
            </Fade>
        </Modal>
    );
};
